

import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
  super.connectedCallback();

  const { signal } = this.#abortController;
  const target = this.closest('.shopify-section, dialog, product-card');
  target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

  document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

  // initial
  setTimeout(() => this.#syncCtaToPurchaseOption(), 0);
  setTimeout(() => this.#syncCtaToPurchaseOption(), 250);

  // on purchase option change (run after Appstle reacts)
  this.addEventListener(
    'change',
    () => {
      setTimeout(() => this.#syncCtaToPurchaseOption(), 0);
      setTimeout(() => this.#syncCtaToPurchaseOption(), 150);
    },
    { signal }
  );
}

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  #syncCtaToPurchaseOption = () => {
  const form = this.querySelector('form');
  if (!form) return;

  const btn = this.querySelector('[ref="addToCartButton"]');
  if (!(btn instanceof HTMLButtonElement)) return;

  const atcLabelEl = btn.querySelector('[data-atc-label]');
  if (!atcLabelEl) return;

  const suffixEl = btn.querySelector('[data-purchase-suffix]'); // "- $59.00"
  const periodEl = btn.querySelector('[data-price-period]');    // "/year"

  const ONE_TIME_LABEL = 'Add to cart';
  const SUB_LABEL = 'Subscribe Now';

  const normalize = (s) =>
    (s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const getRadioMode = () => {
    const checkedRadio = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]:checked'));
    if (!checkedRadio) return null;

    // 1) Prefer the explicit label[for="id"] text (works when input is not wrapped by label)
    let labelText = '';
    if (checkedRadio.id) {
      const explicitLabel = form.querySelector(`label[for="${CSS.escape(checkedRadio.id)}"]`);
      if (explicitLabel) labelText += ` ${explicitLabel.textContent || ''}`;
    }

    // 2) Fallback to a nearby container (Appstle often wraps each option in a row/card)
    const row =
      checkedRadio.closest('[class*="purchase"], [class*="option"], [class*="selling"], fieldset, li, div') || null;
    if (row) labelText += ` ${row.textContent || ''}`;

    // 3) Always include radio attributes
    labelText += ` ${checkedRadio.name || ''} ${checkedRadio.id || ''} ${checkedRadio.value || ''}`;

    const t = normalize(labelText);

    const isOneTime =
      t.includes('one-time') ||
      t.includes('one time') ||
      t.includes('onetime') ||
      t.includes('one-time purchase') ||
      t.includes('single purchase') ||
      t.includes('buy once') ||
      t.includes('onetime purchase');

    const isSub =
      t.includes('subscription') ||
      t.includes('subscribe') ||
      t.includes('autoship') ||
      t.includes('deliver') ||
      t.includes('recurring');

    if (isOneTime && !isSub) return 'onetime';
    if (isSub && !isOneTime) return 'subscription';

    // If both matched (rare), prefer explicit value match
    const v = normalize(checkedRadio.value);
    if (v.includes('one') || v.includes('time') || v.includes('onetime')) return 'onetime';
    if (v.includes('sub') || v.includes('recurr') || v.includes('deliver')) return 'subscription';

    return null;
  };

  
  const extractPricesFromRow = (rowEl) => {
    if (!rowEl) return [];
  
    let text = rowEl.textContent || '';
  
    // If this row contains discount messaging, strip "$10" references from that messaging
    // so we do not accidentally treat the discount amount as the price.
    if (/discount|save/i.test(text)) {
      text = text
        .replace(/save\s*\$?\s*10(?:\.00)?/gi, '')     // "SAVE $10.00"
        .replace(/\$?\s*10(?:\.00)?\s*discount/gi, '') // "$10 discount"
        .replace(/\$?\s*10(?:\.00)?/g, '');            // any leftover "$10" in the row
    }
  
    const matches = text.match(/\$\s*\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g) || [];
  
    const cleaned = matches
      .map((m) => m.replace(/\s+/g, ''))
      .filter(Boolean);
  
    return cleaned.filter((v, i) => cleaned.indexOf(v) === i);
  };

  const setButtonPriceFromSelection = (mode) => {
    if (!suffixEl) return;

    const checkedRadio = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]:checked'));
    const row =
      checkedRadio?.closest('[class*="purchase"], [class*="option"], [class*="selling"], fieldset, li, div') || null;

    const prices = extractPricesFromRow(row);

    // If we find prices in the selected row:
    // - subscription rows often have 2 prices (discounted + compare-at). Use the smallest.
    // - one-time rows usually have 1 price. Use the first.
    let chosen = '';
    if (prices.length) {
      if (mode === 'subscription' && prices.length >= 2) {
        // choose minimum numeric
        const parsed = prices
          .map((p) => ({ raw: p, val: Number(p.replace(/[^0-9.]/g, '')) }))
          .filter((x) => !Number.isNaN(x.val));
        parsed.sort((a, b) => a.val - b.val);
        chosen = parsed[0]?.raw || prices[0];
      } else {
        chosen = prices[0];
      }
    }

    // Fallback: keep whatever the theme already printed (do not overwrite)
    if (!chosen) return;

    // suffix pattern is "- $59.00"
    suffixEl.textContent = `- ${chosen}`;
  };


// RADIO WINS. Only fallback to selling_plan if radio mode is not detectable.
  let isSubscription = false;
  const radioMode = getRadioMode();

  if (radioMode === 'subscription') {
    isSubscription = true;
  } else if (radioMode === 'onetime') {
    isSubscription = false;
  } else {
    // Last-resort fallback: selling_plan truth
    const sellingPlanVal = (form.querySelector('input[name="selling_plan"]')?.value || '').trim();
    isSubscription = !!sellingPlanVal;
  }

  if (isSubscription) {
    atcLabelEl.textContent = SUB_LABEL;
    setButtonPriceFromSelection('subscription');

    if (suffixEl) suffixEl.style.display = '';
    if (periodEl) periodEl.style.display = '';

    btn.classList.add('is-subscription');
    btn.classList.remove('is-onetime');
  } else {
    atcLabelEl.textContent = ONE_TIME_LABEL;
    // One-time price is the base price, keep the first price found.
    setButtonPriceFromSelection('onetime');

    if (suffixEl) suffixEl.style.display = '';
    if (periodEl) periodEl.style.display = 'none';

    btn.classList.add('is-onetime');
    btn.classList.remove('is-subscription');
  }

  // Add small inline badges to the purchase option labels.
  // - One-time: "+ $10 one-time purchase fee"
  // - Subscription: "$10 discount"
  try {
    const ensureBadge = (radioEl, mode) => {
      if (!radioEl) return;

      // Try to find a label tied to the radio.
      /** @type {HTMLElement | null} */
      let labelEl = null;
      if (radioEl.id) {
        labelEl = /** @type {HTMLElement | null} */ (form.querySelector(`label[for="${CSS.escape(radioEl.id)}"]`));
      }
      if (!labelEl) {
        labelEl = /** @type {HTMLElement | null} */ (radioEl.closest('label'));
      }
      if (!labelEl) {
        // Fallback: Appstle-style row wrapper
        labelEl = /** @type {HTMLElement | null} */ (
          radioEl.closest('[class*="purchase"], [class*="option"], [class*="selling"], fieldset, li, div')
        );
      }
      if (!labelEl) return;

      // Only show badge on subscription option.
      if (mode !== 'subscription') return;

      // Avoid duplicates.
      if (labelEl.querySelector('.purchase-badge')) return;

      const badge = document.createElement('span');
      badge.className = 'purchase-badge purchase-badge--discount';
      badge.textContent = '$10 discount';

      // Insert after the first text node if possible, else append.
      labelEl.appendChild(badge);
    };

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (form.querySelectorAll('input[type="radio"]'));
    radios.forEach((r) => {
      const t = normalize(
        `${r.name || ''} ${r.id || ''} ${r.value || ''} ${(r.closest('label')?.textContent || '')} ${(form.querySelector(`label[for="${CSS.escape(r.id || '')}"]`)?.textContent || '')}`
      );

      const isOneTime =
        t.includes('one-time') ||
        t.includes('one time') ||
        t.includes('onetime') ||
        t.includes('one-time purchase') ||
        t.includes('single purchase') ||
        t.includes('buy once') ||
        t.includes('onetime purchase');

      const isSub =
        t.includes('subscription') ||
        t.includes('subscribe') ||
        t.includes('autoship') ||
        t.includes('deliver') ||
        t.includes('recurring');

      if (isSub && !isOneTime) ensureBadge(r, 'subscription');
      if (isOneTime && !isSub) ensureBadge(r, 'onetime');
    });
  } catch (e) {
    // no-op
  }

  // Keep the on-page price the same for both one-time and subscription.
  // The +$10 fee is applied at cart/checkout via the hidden fee variant.
  try {
    const priceHost =
      /** @type {HTMLElement | null} */ (this.closest('main')?.querySelector('product-price')) ||
      /** @type {HTMLElement | null} */ (this.querySelector('product-price'));

    if (priceHost) {
      const base = parseInt(priceHost.getAttribute('data-base-price') || '0', 10);

      const formatMoney = (cents) => {
        const fmt = priceHost.getAttribute('data-money-format') || '${{amount}}';
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, fmt);
        }
        // Fallback: very basic USD formatting
        return '$' + (cents / 100).toFixed(2);
      };

      if (base > 0) {
        const priceEl =
          priceHost.querySelector('.price') ||
          priceHost.querySelector('[data-price]') ||
          priceHost.querySelector('span');

        if (priceEl) priceEl.textContent = formatMoney(base);
      }
    }
  } catch (e) {
    // no-op
  }

};

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');
    
    this.#syncCtaToPurchaseOption();

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

// Appstle/Horizon integration guard:
// 1) If selling_plan is empty, remove it.
// 2) If user selected one-time, force remove selling_plan.
const sellingPlanVal = (formData.get('selling_plan') || '').toString().trim();
if (!sellingPlanVal) {
  formData.delete('selling_plan');
}

// Extra safety: if a "One-Time Purchase" radio is checked, strip selling_plan.
// (Selectors are intentionally flexible since Appstle markup varies.)
const checkedRadio = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]:checked'));
if (checkedRadio) {
  const haystack = `${checkedRadio.name} ${checkedRadio.id} ${checkedRadio.value}`.toLowerCase();
  const isOneTime =
    haystack.includes('one') &&
    (haystack.includes('time') || haystack.includes('onetime') || haystack.includes('one-time'));

  if (isOneTime) {
    formData.delete('selling_plan');
  }
}

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
 #onVariantUpdate = async (event) => {
  if (event.detail.data.newProduct) {
    this.dataset.productId = event.detail.data.newProduct.id;
  } else if (event.detail.data.productId !== this.dataset.productId) {
    return;
  }

  const { variantId } = this.refs;

  // Update the variant ID
  variantId.value = event.detail.resource?.id ?? '';

  const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
  const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

  // Update state and text for add-to-cart button
  if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

  // Update the button state
  if (event.detail.resource == null || event.detail.resource.available == false) {
    currentAddToCartButtonContainer.disable();
  } else {
    currentAddToCartButtonContainer.enable();
  }

  const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
  if (newAddToCartButton && currentAddToCartButton) {
    morph(currentAddToCartButton, newAddToCartButton);
  }

  // IMPORTANT: Horizon morphs the button + CTA markup above, so re-sync AFTER morph
  this.#syncCtaToPurchaseOption();
  setTimeout(() => this.#syncCtaToPurchaseOption(), 0);
  setTimeout(() => this.#syncCtaToPurchaseOption(), 150);

  if (acceleratedCheckoutButtonContainer) {
    if (event.detail.resource == null || event.detail.resource.available == false) {
      acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
  }

  // Set the data attribute for the product variant media if it exists
  if (event.detail.resource) {
    const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
    if (productVariantMedia) {
      this.refs.addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  }

  // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
  const { quantityRules, pricePerItem, quantitySelector, productFormButtons, quantityLabel, quantitySelectorWrapper } =
    this.refs;

  // Update quantity selector's min/max/step attributes and cart quantity for the new variant
  const newQuantityInput = /** @type {HTMLInputElement | null} */ (
    event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
  );

  if (quantitySelector?.updateConstraints && newQuantityInput) {
    quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
  }

  const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
  const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

  const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
  const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

  if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
    // Store quantity value before morphing entire container
    const currentQuantityValue = quantitySelector.getValue?.();

    const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

    if (productFormButtons && newProductFormButtons) {
      morph(productFormButtons, newProductFormButtons);

      // Get the NEW quantity selector after morphing and update its constraints
      const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
        event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
        // Temporarily set the old value so updateConstraints can snap it properly
        this.refs.quantitySelector.setValue(currentQuantityValue);
        // updateConstraints will snap to valid increment if needed
        this.refs.quantitySelector.updateConstraints(
          newQuantityInputElement.min,
          newQuantityInputElement.max || null,
          newQuantityInputElement.step
        );
      }
    }
  } else {
    // Update elements individually when layout isn't changing
    /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
    const morphTargets = [
      ['.quantity-label', quantityLabel, quantitySelector],
      ['.quantity-rules', quantityRules, this.refs.productFormButtons],
      ['price-per-item', pricePerItem, quantitySelectorWrapper],
    ];

    for (const [selector, currentElement, fallback] of morphTargets) {
      this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
    }
  }

  // Morph volume pricing if it exists
  const currentVolumePricing = this.refs.volumePricing;
  const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
  this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

  const hasB2BFeatures =
    quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

  if (!hasB2BFeatures) return;

  // Fetch and update cart quantity for the new variant
  await this.#fetchAndUpdateCartQuantity();
};

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
